import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import {
  getOrganizationSiteTree,
  deleteSite,
  createSite,
  updateSite,
  UpdateSiteParams,
} from "../services/organization.ts";
import SiteTreePanel from "../components/SiteTreePanel.tsx";
import CombinedDevicePanel from "../components/CombinedDevicePanel.tsx";
import AddSiteModal from "../components/Modals/AddSiteModal.tsx";
// AddGWDeviceModal import removed - focusing on IQWatch devices only
import AddIQWatchDeviceModal from "../components/Modals/AddIQWatchDeviceModal.tsx";
import { Organization, Site, IQWatchDevice } from "../types";
// Edge Device service imports removed - focusing on IQWatch devices only
import {
  createIQWatchDevice,
  updateIQWatchDevice,
  deleteIQWatchDevice,
} from "../services/iqwatchService.ts";
import messageService from "../components/Message/message.ts";

// 用于递归转换后端 Site 为前端 Site 类型
function convertApiSiteToFrontendSite(apiSite, orgId): Site {
  return {
    id: apiSite.id,
    name: apiSite.name,
    type:
      apiSite.type === "node_type"
        ? "building"
        : apiSite.type === "region_type"
        ? "region"
        : apiSite.type === "location_type"
        ? "location"
        : "building",
    organizationId: orgId,
    parentId: apiSite.parent_id,
    children: apiSite.children
      ? apiSite.children.map((child) =>
          convertApiSiteToFrontendSite(child, orgId)
        )
      : [],
    deviceCount:
      (apiSite.edge_devices ? apiSite.edge_devices.length : 0) +
      (apiSite.iqwatch_devices ? apiSite.iqwatch_devices.length : 0),
    createdAt: apiSite.created_at,
    updatedAt: apiSite.updated_at,
    detail:
      typeof apiSite.detail === "string"
        ? (() => {
            try {
              return JSON.parse(apiSite.detail);
            } catch (error) {
              console.warn(
                "Failed to parse site detail JSON:",
                apiSite.detail,
                error
              );
              return {};
            }
          })()
        : apiSite.detail || {},
    created_at: apiSite.created_at,
    updated_at: apiSite.updated_at,
    org_id: orgId,
    edge_devices: apiSite.edge_devices || [],
    iqwatch_devices: apiSite.iqwatch_devices || [],
  } as Site;
}

const OrganizationDetail: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  // 站点管理状态
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [creatingSite, setCreatingSite] = useState(false);

  // Edge device management state removed - focusing on IQWatch devices only

  // iQWatch 设备管理状态
  const [showIQWatchModal, setShowIQWatchModal] = useState(false);
  const [editingIQWatchDevice, setEditingIQWatchDevice] =
    useState<IQWatchDevice | null>(null);
  const [creatingIQWatchDevice, setCreatingIQWatchDevice] = useState(false);

  // 删除确认状态
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState<{
    type: "site" | "device" | "iqwatch";
    id: string;
    name: string;
  } | null>(null);

  const fetchOrganizationData = useCallback(async () => {
    try {
      setLoading(true);
      if (orgId) {
        const data = await getOrganizationSiteTree(orgId);
        setOrganization({
          ...data.organization,
          description: "",
          sites: data.sites.map((site) =>
            convertApiSiteToFrontendSite(site, data.organization.id)
          ),
          deviceCount: 0, // total_edge_devices removed - focusing on IQWatch devices only
        });
      }
    } catch (error) {
      console.error("Failed to fetch organization data:", error);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) {
      fetchOrganizationData();
    }
  }, [orgId, fetchOrganizationData]);

  useEffect(() => {
    if (!organization || !selectedSite) return;
    // 在新 organization.sites 里递归查找同 id 的 site
    function findSiteById(sites, id) {
      for (const site of sites) {
        if (site.id === id) return site;
        if (site.children) {
          const found = findSiteById(site.children, id);
          if (found) return found;
        }
      }
      return null;
    }
    const updatedSite = findSiteById(organization.sites, selectedSite.id);
    if (updatedSite) setSelectedSite(updatedSite);
    // 如果找不到，说明被删了，可以 setSelectedSite(null)
  }, [organization, selectedSite]);

  // 站点操作
  const handleCreateSite = async (formData: any) => {
    try {
      setCreatingSite(true);
      const siteData = {
        parentId: formData.parentId || undefined,
        site: {
          name: formData.name,
          siteType: formData.siteType,
        },
        org_id: orgId || "",
      };

      await createSite(siteData);
      setShowSiteModal(false);
      setEditingSite(null);
      fetchOrganizationData();
    } catch (error) {
      console.error("Failed to create site:", error);
      throw error; // 重新抛出错误，让 AddSiteModal 处理
    } finally {
      setCreatingSite(false);
    }
  };

  const handleEditSite = async (formData: any) => {
    try {
      setCreatingSite(true);

      // Debug: Log the received form data
      console.log("Form data received:", formData);

      // Convert frontend site type back to backend format
      const backendSiteType =
        formData.siteType === "region"
          ? "region_type"
          : formData.siteType === "building"
          ? "node_type"
          : formData.siteType === "location"
          ? "location_type"
          : formData.siteType;

      // Convert form data to UpdateSiteParams
      const updateData: UpdateSiteParams = {
        name: formData.name,
        siteType: backendSiteType,
        detail: formData.detail,
        parentId: formData.parentId,
      };

      // Debug: Log what's being sent to API
      console.log("Update data being sent:", updateData);

      await updateSite(editingSite!.id, updateData);
      setShowSiteModal(false);
      setEditingSite(null);
      fetchOrganizationData();
    } catch (error) {
      console.error("Failed to update site:", error);
      throw error;
    } finally {
      setCreatingSite(false);
    }
  };

  const handleSiteSubmit = async (formData: any) => {
    if (editingSite) {
      await handleEditSite(formData);
    } else {
      await handleCreateSite(formData);
    }
  };

  const handleDeleteSite = async () => {
    if (!deleteItem || deleteItem.type !== "site") return;

    try {
      await deleteSite(deleteItem.id);
      setShowDeleteModal(false);
      setDeleteItem(null);
      fetchOrganizationData();
    } catch (error) {
      console.error("Failed to delete site:", error);
    }
  };

  const openCreateSiteModal = () => {
    setEditingSite(null);
    setShowSiteModal(true);
  };

  const openEditSiteModal = (site: Site) => {
    setEditingSite(site);
    setShowSiteModal(true);
  };

  const openDeleteSiteModal = (site: Site) => {
    setDeleteItem({
      type: "site",
      id: site.id,
      name: site.name,
    });
    setShowDeleteModal(true);
  };

  // 获取所有可选的父站点（排除当前编辑的站点及其子站点）
  const getAvailableParentSites = (): Site[] => {
    if (!organization) return [];

    const excludeIds = new Set<string>();
    if (editingSite) {
      excludeIds.add(editingSite.id);
      // 递归添加所有子站点ID
      const addChildIds = (site: Site) => {
        site.children?.forEach((child) => {
          excludeIds.add(child.id);
          addChildIds(child);
        });
      };
      addChildIds(editingSite);
    }

    const flattenSites = (sites: Site[]): Site[] => {
      return sites.reduce((acc, site) => {
        if (!excludeIds.has(site.id)) {
          acc.push(site);
        }
        if (site.children) {
          acc.push(...flattenSites(site.children));
        }
        return acc;
      }, [] as Site[]);
    };

    return flattenSites(organization.sites);
  };

  // 设备操作
  // handleCreateDevice removed - focusing on IQWatch devices only

  // handleEditDevice removed - focusing on IQWatch devices only

  // handleDeviceSubmit removed - focusing on IQWatch devices only

  const handleDeleteDevice = async () => {
    if (!deleteItem || deleteItem.type !== "device") return;

    try {
      // deleteEdgeDevice call removed - focusing on IQWatch devices only
      setShowDeleteModal(false);
      setDeleteItem(null);
      fetchOrganizationData();
    } catch (error) {
      console.error("Failed to delete device:", error);
    }
  };

  // openCreateDeviceModal removed - focusing on IQWatch devices only

  // openEditDeviceModal removed - focusing on IQWatch devices only

  // openDeleteDeviceModal removed - focusing on IQWatch devices only

  // iQWatch 设备操作
  const handleCreateIQWatchDevice = async (formData: any) => {
    setCreatingIQWatchDevice(true);
    try {
      console.log(
        "OrganizationDetail: About to call createIQWatchDevice with:",
        formData
      );
      await createIQWatchDevice(formData);
      console.log("OrganizationDetail: createIQWatchDevice succeeded");
      // Only execute these if successful
      setShowIQWatchModal(false);
      setEditingIQWatchDevice(null);
      fetchOrganizationData();
      // Success message is now handled in the modal
    } catch (error: any) {
      console.error(
        "OrganizationDetail: Failed to create iQWatch device:",
        error
      );
      // Re-throw the error so the modal can handle it
      throw error;
    } finally {
      setCreatingIQWatchDevice(false);
    }
  };

  const handleEditIQWatchDevice = async (formData: any) => {
    setCreatingIQWatchDevice(true);
    try {
      await updateIQWatchDevice(editingIQWatchDevice!.id, formData);
      // Only execute these if successful
      setShowIQWatchModal(false);
      setEditingIQWatchDevice(null);
      fetchOrganizationData();
      // Success message is now handled in the modal
    } catch (error: any) {
      console.error("Failed to update iQWatch device:", error);
      // Re-throw the error so the modal can handle it
      throw error;
    } finally {
      setCreatingIQWatchDevice(false);
    }
  };

  const handleIQWatchDeviceSubmit = async (formData: any) => {
    if (editingIQWatchDevice) {
      await handleEditIQWatchDevice(formData);
    } else {
      await handleCreateIQWatchDevice(formData);
    }
  };

  const handleDeleteIQWatchDevice = async () => {
    if (!deleteItem || deleteItem.type !== "iqwatch") return;

    try {
      await deleteIQWatchDevice(deleteItem.id);
      messageService.success(
        `IQWatch device "${deleteItem.name}" deleted successfully!`
      );
      setShowDeleteModal(false);
      setDeleteItem(null);
      fetchOrganizationData();
    } catch (error: any) {
      console.error("Failed to delete iQWatch device:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete IQWatch device";
      messageService.error(errorMessage);
    }
  };

  const openCreateIQWatchDeviceModal = () => {
    setEditingIQWatchDevice(null);
    setShowIQWatchModal(true);
  };

  const openEditIQWatchDeviceModal = (device: IQWatchDevice) => {
    setEditingIQWatchDevice(device);
    setShowIQWatchModal(true);
  };

  const openDeleteIQWatchDeviceModal = (device: IQWatchDevice) => {
    setDeleteItem({
      type: "iqwatch",
      id: device.id,
      name: device.name,
    });
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600">Organization not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 sm:p-6 overflow-x-auto">
      <div className="max-w-7xl mx-auto min-w-[900px]">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/organization")}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Organizations
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {organization.name}
              </h1>
              <p className="text-gray-600 mt-1">{organization.description}</p>
              <p className="text-gray-600 mt-1">Code: {organization.code}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={openCreateSiteModal}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Site
              </button>
            </div>
          </div>
        </div>

        {/* 只保留一个tab */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button className="py-2 px-1 border-b-2 font-medium text-sm flex items-center border-green-500 text-green-600">
              Sites & Devices
            </button>
          </nav>
        </div>

        {/* 主体内容：左右两栏 */}
        <div className="bg-white rounded-lg shadow-sm border flex h-auto min-h-[400px]">
          <div className="border-r">
            <SiteTreePanel
              organizations={organization ? [organization] : []}
              selectedSite={selectedSite}
              onSiteSelect={setSelectedSite}
              onEditSite={openEditSiteModal}
              onDeleteSite={openDeleteSiteModal}
              enableEditMode={true}
            />
          </div>
          <CombinedDevicePanel
            // devices prop removed - focusing on IQWatch devices only
            site={selectedSite}
            orgId={orgId}
            // Edge device handlers removed - focusing on IQWatch devices only
            onEditIQWatchDevice={openEditIQWatchDeviceModal}
            onDeleteIQWatchDevice={openDeleteIQWatchDeviceModal}
            onAddIQWatchDevice={openCreateIQWatchDeviceModal}
          />
        </div>

        {/* Site Modal */}
        <AddSiteModal
          isOpen={showSiteModal}
          onClose={() => {
            setShowSiteModal(false);
            setEditingSite(null);
          }}
          onSubmit={handleSiteSubmit}
          site={editingSite}
          loading={creatingSite}
          parentSites={getAvailableParentSites()}
        />

        {/* Device Modal */}
        {/* AddGWDeviceModal removed - focusing on IQWatch devices only */}

        {/* iQWatch Device Modal */}
        <AddIQWatchDeviceModal
          isOpen={showIQWatchModal}
          onClose={() => {
            setShowIQWatchModal(false);
            setEditingIQWatchDevice(null);
          }}
          onSubmit={handleIQWatchDeviceSubmit}
          device={editingIQWatchDevice}
          loading={creatingIQWatchDevice}
          siteId={selectedSite?.id || ""}
          orgId={orgId || ""}
          onDeviceListRefresh={fetchOrganizationData}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deleteItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center mb-4">
                <Trash2 className="w-6 h-6 text-red-600 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Delete{" "}
                  {deleteItem.type === "site" ? "Site" : "iQWatch Device"}
                </h2>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{deleteItem.name}"? This action
                cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    deleteItem.type === "site"
                      ? handleDeleteSite
                      : deleteItem.type === "device"
                      ? handleDeleteDevice
                      : handleDeleteIQWatchDevice
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationDetail;
